<template>
  <div class="student-exam-info">
    <!-- 页面头部 -->
    <div class="page-header">
      <h3>考试信息</h3>
      <div class="header-actions">
        <el-button type="primary" icon="el-icon-refresh" @click="refreshData">刷新</el-button>
      </div>
    </div>

    <!-- 考试状态统计 -->
    <div class="exam-stats" v-if="!loading">
      <el-row :gutter="20">
        <el-col :span="8">
          <div class="stat-card">
            <div class="stat-icon pending">
              <i class="el-icon-time"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ pendingExams.length }}</div>
              <div class="stat-label">未完成考试</div>
            </div>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="stat-card">
            <div class="stat-icon completed">
              <i class="el-icon-check"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ completedExams.length }}</div>
              <div class="stat-label">已完成考试</div>
            </div>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="stat-card">
            <div class="stat-icon total">
              <i class="el-icon-document"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ allExams.length }}</div>
              <div class="stat-label">总考试数</div>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 考试列表 -->
    <div class="exam-content" v-loading="loading">
      <!-- 未完成考试 -->
      <div class="exam-section" v-if="pendingExams.length > 0">
        <div class="section-header">
          <h4><i class="el-icon-time"></i> 未完成考试</h4>
          <el-tag type="warning">{{ pendingExams.length }} 个</el-tag>
        </div>
        <div class="exam-list">
          <div v-for="exam in pendingExams" :key="exam.id" class="exam-item pending">
            <div class="exam-info">
              <div class="exam-title">{{ exam.examName }}</div>
              <div class="exam-desc">{{ exam.examDescription || '暂无描述' }}</div>
              <div class="exam-meta">
                <span class="meta-item">
                  <i class="el-icon-time"></i>
                  考试时间：{{ formatExamTime(exam.startTime, exam.endTime) }}
                </span>
                <span class="meta-item">
                  <i class="el-icon-timer"></i>
                  时长：{{ exam.duration }} 分钟
                </span>
                <span class="meta-item">
                  <i class="el-icon-medal"></i>
                  总分：{{ exam.totalScore }} 分
                </span>
              </div>
            </div>
            <div class="exam-actions">
              <el-tag type="warning" size="small">未开始</el-tag>
            </div>
          </div>
        </div>
      </div>

      <!-- 已完成考试 -->
      <div class="exam-section" v-if="completedExams.length > 0">
        <div class="section-header">
          <h4><i class="el-icon-check"></i> 已完成考试</h4>
          <el-tag type="success">{{ completedExams.length }} 个</el-tag>
        </div>
        <div class="exam-list">
          <div v-for="exam in completedExams" :key="exam.id" class="exam-item completed">
            <div class="exam-info">
              <div class="exam-title">{{ exam.examName }}</div>
              <div class="exam-desc">{{ exam.examDescription || '暂无描述' }}</div>
              <div class="exam-meta">
                <span class="meta-item">
                  <i class="el-icon-time"></i>
                  考试时间：{{ formatExamTime(exam.startTime, exam.endTime) }}
                </span>
                <span class="meta-item">
                  <i class="el-icon-timer"></i>
                  时长：{{ exam.duration }} 分钟
                </span>
                <span class="meta-item">
                  <i class="el-icon-medal"></i>
                  总分：{{ exam.totalScore }} 分
                </span>
                <span class="meta-item" v-if="exam.score !== null && exam.score !== undefined">
                  <i class="el-icon-star-on"></i>
                  得分：{{ exam.score }} 分
                </span>
              </div>
            </div>
            <div class="exam-actions">
              <el-button type="primary" size="small" @click="viewExamDetail(exam)">
                查看详情
              </el-button>
              <el-tag :type="getScoreTagType(exam.score, exam.totalScore)" size="small">
                {{ getScoreText(exam.score, exam.totalScore) }}
              </el-tag>
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-if="!loading && allExams.length === 0" class="empty-state">
        <i class="el-icon-document"></i>
        <p>暂无考试信息</p>
      </div>
    </div>
  </div>
</template>

<script>
import { examApi } from '@/api/exam'

export default {
  name: 'StudentExamInfo',
  props: {
    courseId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      allExams: [],
      pendingExams: [],
      completedExams: []
    }
  },
  mounted() {
    this.loadExamData()
  },
  methods: {
    // 加载考试数据
    async loadExamData() {
      this.loading = true
      try {
        console.log('StudentExamInfo - 开始加载考试数据')
        console.log('StudentExamInfo - courseId:', this.courseId)
        console.log('StudentExamInfo - courseId类型:', typeof this.courseId)

        // 获取课程下的所有考试
        const response = await examApi.getExamsByCourse(this.courseId)
        console.log('StudentExamInfo - API响应:', response)
        console.log('StudentExamInfo - 响应状态码:', response?.code)
        console.log('StudentExamInfo - 响应数据:', response?.data)

        if (response && response.code === 1) {
          this.allExams = response.data || []
          console.log('StudentExamInfo - 设置allExams:', this.allExams)
          console.log('StudentExamInfo - allExams长度:', this.allExams.length)
          this.categorizeExams()
        } else {
          console.error('StudentExamInfo - API返回失败:', response)
          this.$message.error(response?.msg || '获取考试信息失败')
        }
      } catch (error) {
        console.error('StudentExamInfo - 加载考试数据失败:', error)
        console.error('StudentExamInfo - 错误详情:', error.response)
        this.$message.error('加载考试数据失败')
      } finally {
        this.loading = false
      }
    },

    // 分类考试
    categorizeExams() {
      const now = new Date()

      this.pendingExams = []
      this.completedExams = []

      this.allExams.forEach(exam => {
        const startTime = new Date(exam.startTime)
        const endTime = new Date(exam.endTime)

        // 判断考试状态
        if (now < startTime) {
          // 未开始
          this.pendingExams.push({
            ...exam,
            status: 'pending'
          })
        } else if (now >= startTime && now <= endTime) {
          // 进行中，但学生端不提供在线考试功能
          this.pendingExams.push({
            ...exam,
            status: 'in_progress'
          })
        } else {
          // 已结束
          this.completedExams.push({
            ...exam,
            status: 'completed'
          })
        }
      })
    },

    // 刷新数据
    async refreshData() {
      await this.loadExamData()
    },

    // 格式化考试时间
    formatExamTime(startTime, endTime) {
      const start = new Date(startTime)
      const end = new Date(endTime)

      const startStr = start.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })

      const endStr = end.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })

      return `${startStr} - ${endStr}`
    },

    // 获取分数标签类型
    getScoreTagType(score, totalScore) {
      if (score === null || score === undefined) return 'info'

      const percentage = (score / totalScore) * 100
      if (percentage >= 90) return 'success'
      if (percentage >= 80) return 'primary'
      if (percentage >= 60) return 'warning'
      return 'danger'
    },

    // 获取分数文本
    getScoreText(score, totalScore) {
      if (score === null || score === undefined) return '未评分'
      return `${score}/${totalScore}`
    },

    // 查看考试详情
    viewExamDetail(exam) {
      // 在课程详情页面中打开考试详情
      this.$emit('view-exam-detail', exam)
    }
  }
}
</script>

<style scoped>
.student-exam-info {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ebeef5;
}

.page-header h3 {
  margin: 0;
  color: #303133;
  font-size: 18px;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* 统计卡片 */
.exam-stats {
  margin-bottom: 30px;
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #409EFF;
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-size: 24px;
  color: #fff;
}

.stat-icon.pending {
  background: linear-gradient(135deg, #ff9500, #ff6b00);
}

.stat-icon.completed {
  background: linear-gradient(135deg, #67c23a, #4caf50);
}

.stat-icon.total {
  background: linear-gradient(135deg, #409eff, #2196f3);
}

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 28px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 14px;
  color: #909399;
}

/* 考试部分 */
.exam-section {
  margin-bottom: 30px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding: 10px 0;
  border-bottom: 2px solid #f0f0f0;
}

.section-header h4 {
  margin: 0;
  color: #303133;
  font-size: 16px;
  display: flex;
  align-items: center;
}

.section-header h4 i {
  margin-right: 8px;
  color: #409EFF;
}

/* 考试列表 */
.exam-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.exam-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #e4e7ed;
  transition: all 0.3s ease;
}

.exam-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.exam-item.pending {
  border-left-color: #ff9500;
}

.exam-item.completed {
  border-left-color: #67c23a;
}

.exam-info {
  flex: 1;
}

.exam-title {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 8px;
}

.exam-desc {
  font-size: 14px;
  color: #606266;
  margin-bottom: 12px;
  line-height: 1.4;
}

.exam-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.meta-item {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #909399;
}

.meta-item i {
  margin-right: 4px;
  color: #409EFF;
}

.exam-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}

.empty-state i {
  font-size: 64px;
  margin-bottom: 20px;
  color: #c0c4cc;
}

.empty-state p {
  font-size: 16px;
  margin: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .exam-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .exam-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .exam-meta {
    flex-direction: column;
    gap: 8px;
  }

  .stat-card {
    flex-direction: column;
    text-align: center;
  }

  .stat-icon {
    margin-right: 0;
    margin-bottom: 10px;
  }
}
</style>
